import { computed, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';

import type { BaseMetaState } from '@core/ngrx-legacy';
import {
  TellentAssistantCurrentThreadActions,
  TellentAssistantCurrentThreadSelectors,
  TellentAssistantThreadsActions,
  TellentAssistantThreadsSelectors,
  type CurrentThreadMessages,
} from '@core/tellent-assistant-data';
import type { ThreadSummary } from '@core/tellent-assistant-types';

@Injectable({ providedIn: 'root' })
export class TellentAssistantThreadsService {
  private readonly store$ = inject(Store);
  private readonly threadMetaMap = toSignal(
    this.store$.select(TellentAssistantThreadsSelectors.entityMetaMap),
    { initialValue: <Record<string, BaseMetaState>>{} },
  );

  private readonly rawThreads = toSignal(
    this.store$.select(TellentAssistantThreadsSelectors.allEntities),
    { initialValue: <ThreadSummary[]>[] },
  );

  public readonly threads = computed(() =>
    [...this.rawThreads()].sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return b.updatedAt.getTime() - a.updatedAt.getTime();
    }),
  );

  public readonly currentThread: Signal<CurrentThreadMessages | null> = toSignal(
    this.store$.select(TellentAssistantCurrentThreadSelectors.data),
    { initialValue: null },
  );

  public readonly currentThreadLoading = toSignal(
    this.store$.select(TellentAssistantCurrentThreadSelectors.isPending),
    { initialValue: false },
  );

  public loadThreads(): void {
    this.store$.dispatch(TellentAssistantThreadsActions.fetchCollection());
  }

  public loadCurrent(threadId: string): void {
    this.store$.dispatch(TellentAssistantCurrentThreadActions.fetch({ payload: { threadId } }));
  }

  public deleteThread(threadId: string): void {
    this.store$.dispatch(
      TellentAssistantThreadsActions.removeOnePessimistic({ payload: { data: threadId } }),
    );
  }

  public setThreadPinned(threadId: string, pinned: boolean): void {
    this.store$.dispatch(
      TellentAssistantThreadsActions.updateOnePessimistic({
        payload: { data: { id: threadId, pinned } },
      }),
    );
  }

  public isThreadDeleting(threadId: string): boolean {
    const meta = this.threadMetaMap()[threadId];
    return meta?.pending ?? false;
  }
}
